/**
 * AdminRegistrationEditModal
 * --------------------------
 * Modal de "edición total" para inscripciones, reservado a admin de
 * asociación / unión. Permite:
 *   - Editar campos (categoría, precio unitario, notas, alergias, etc.)
 *   - Cambiar el estado a libertad (pending/approved/rejected/cancelled/waitlist)
 *   - Transferir la inscripción a otro club
 *   - Reactivar inscripciones canceladas/rechazadas
 *   - Eliminar definitivamente (con confirmación)
 *
 * Cada acción registra el cambio en `eventRegistrationAudit`.
 */

import { useMemo, useState } from "react";
import {
  Modal,
  ModalFooter,
  Button,
  Input,
  Select,
  Badge,
} from "@/components/ui";
import { useConfirm } from "@/components/ui/ConfirmDialog";
import {
  useAdminEditRegistration,
  useAdminTransferRegistration,
  useAdminForceStatusRegistration,
  useAdminReactivateRegistration,
  useAdminDeleteRegistration,
} from "@/hooks/useEvents";
import { useClubs } from "@/hooks/useOrganization";
import {
  AlertTriangle,
  ArrowRightLeft,
  RotateCcw,
  Save,
  Trash2,
  Users,
} from "lucide-react";
import type { EventRegistration, RegistrationStatus } from "@/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  registration: EventRegistration | null;
  /**
   * Abre el flujo de "intercambiar integrante" para esta inscripción.
   * Si no se pasa, no se muestra la opción.
   */
  onSwap?: (registration: EventRegistration) => void;
}

const CATEGORY_OPTIONS: {
  value: EventRegistration["category"];
  label: string;
}[] = [
  { value: "pathfinder", label: "Conquistador / Aventurero" },
  { value: "staff", label: "Staff / Consejero" },
  { value: "guest", label: "Invitado" },
  { value: "family", label: "Familia" },
];

const STATUS_OPTIONS: { value: RegistrationStatus; label: string }[] = [
  { value: "pending", label: "Pendiente" },
  { value: "approved", label: "Aprobada" },
  { value: "waitlist", label: "Lista de espera" },
  { value: "rejected", label: "Rechazada" },
  { value: "cancelled", label: "Cancelada" },
];

const STATUS_BADGE: Record<RegistrationStatus, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  waitlist: "bg-blue-100 text-blue-700",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-700",
};

export default function AdminRegistrationEditModal({
  isOpen,
  onClose,
  registration,
  onSwap,
}: Props) {
  const confirm = useConfirm();
  const { data: clubs } = useClubs();

  const editMutation = useAdminEditRegistration();
  const transferMutation = useAdminTransferRegistration();
  const forceStatusMutation = useAdminForceStatusRegistration();
  const reactivateMutation = useAdminReactivateRegistration();
  const deleteMutation = useAdminDeleteRegistration();

  // Local form state, initialised when modal opens with a registration
  const [category, setCategory] = useState<EventRegistration["category"]>(
    registration?.category ?? "pathfinder",
  );
  const [unitPrice, setUnitPrice] = useState<number>(
    registration?.unitPrice ?? 0,
  );
  const [notes, setNotes] = useState<string>(registration?.notes ?? "");
  const [medicalNotes, setMedicalNotes] = useState<string>(
    registration?.medicalNotes ?? "",
  );
  const [dietaryRestrictions, setDietaryRestrictions] = useState<string>(
    registration?.dietaryRestrictions ?? "",
  );
  const [status, setStatus] = useState<RegistrationStatus>(
    registration?.status ?? "pending",
  );
  const [targetClubId, setTargetClubId] = useState<string>(
    registration?.clubId ?? "",
  );
  const [reason, setReason] = useState("");

  // Reset local state when registration changes
  useMemo(() => {
    if (!registration) return;
    setCategory(registration.category);
    setUnitPrice(registration.unitPrice);
    setNotes(registration.notes ?? "");
    setMedicalNotes(registration.medicalNotes ?? "");
    setDietaryRestrictions(registration.dietaryRestrictions ?? "");
    setStatus(registration.status);
    setTargetClubId(registration.clubId);
    setReason("");
  }, [registration?.id]);

  if (!registration) return null;

  const reg = registration; // alias for type-narrowing
  const targetClub = clubs?.find((c) => c.id === targetClubId);
  const isInactive = ["cancelled", "rejected"].includes(reg.status);
  const isAnyMutationPending =
    editMutation.isPending ||
    transferMutation.isPending ||
    forceStatusMutation.isPending ||
    reactivateMutation.isPending ||
    deleteMutation.isPending;

  const fieldChanges =
    category !== reg.category ||
    unitPrice !== reg.unitPrice ||
    (notes ?? "") !== (reg.notes ?? "") ||
    (medicalNotes ?? "") !== (reg.medicalNotes ?? "") ||
    (dietaryRestrictions ?? "") !== (reg.dietaryRestrictions ?? "");
  const statusChanged = status !== reg.status;
  const clubChanged = targetClubId !== reg.clubId && !!targetClub;

  const handleSaveAll = async () => {
    // 1) Field edits
    if (fieldChanges) {
      await editMutation.mutateAsync({
        registrationId: reg.id,
        eventId: reg.eventId,
        patch: {
          category,
          unitPrice,
          notes,
          medicalNotes,
          dietaryRestrictions,
        },
        reason: reason || undefined,
      });
    }
    // 2) Club transfer
    if (clubChanged && targetClub) {
      await transferMutation.mutateAsync({
        registrationId: reg.id,
        eventId: reg.eventId,
        targetClubId: targetClub.id,
        targetClubName: targetClub.name,
        reason: reason || undefined,
      });
    }
    // 3) Status change
    if (statusChanged) {
      await forceStatusMutation.mutateAsync({
        registrationId: reg.id,
        eventId: reg.eventId,
        status,
        reason: reason || undefined,
      });
    }
    if (fieldChanges || clubChanged || statusChanged) {
      onClose();
    }
  };

  const handleReactivate = async () => {
    await reactivateMutation.mutateAsync({
      registrationId: reg.id,
      eventId: reg.eventId,
      reason: reason || undefined,
    });
    onClose();
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: "Eliminar inscripción definitivamente",
      description: `¿Eliminar la inscripción de ${reg.personName}? Esta acción no se puede deshacer. Los pagos del club no se eliminan (son por club).`,
      confirmText: "Eliminar",
      cancelText: "Cancelar",
      variant: "danger",
    });
    if (!ok) return;
    await deleteMutation.mutateAsync({
      registrationId: reg.id,
      eventId: reg.eventId,
      reason: reason || undefined,
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Editar inscripción · ${reg.personName}`}
      description={`${reg.eventName} · Club actual: ${reg.clubName}`}
      size="lg"
    >
      <div className="space-y-6 px-1">
        {/* Estado actual */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Estado actual:</span>
          <Badge className={STATUS_BADGE[reg.status]}>{reg.status}</Badge>
        </div>

        {/* Sección: campos editables */}
        <section className="border rounded-xl p-4 space-y-3 bg-gray-50/50">
          <h3 className="text-sm font-semibold text-gray-700">Datos</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select
              label="Categoría"
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as EventRegistration["category"])
              }
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <Input
              type="number"
              label="Precio unitario"
              value={unitPrice}
              min={0}
              onChange={(e) => setUnitPrice(Number(e.target.value || 0))}
            />
          </div>
          <Input
            label="Observaciones"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notas internas (opcional)"
          />
          <Input
            label="Notas médicas"
            value={medicalNotes}
            onChange={(e) => setMedicalNotes(e.target.value)}
            placeholder="Alergias, condiciones, medicamentos…"
          />
          <Input
            label="Restricciones alimentarias"
            value={dietaryRestrictions}
            onChange={(e) => setDietaryRestrictions(e.target.value)}
            placeholder="Vegetariano, sin gluten…"
          />
        </section>

        {/* Sección: cambio de estado */}
        <section className="border rounded-xl p-4 space-y-3 bg-gray-50/50">
          <h3 className="text-sm font-semibold text-gray-700">Estado</h3>
          <Select
            label="Forzar estado"
            value={status}
            onChange={(e) => setStatus(e.target.value as RegistrationStatus)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <p className="text-xs text-gray-500">
            Esto recalcula los contadores del evento y el ranking del club.
          </p>
          {isInactive && (
            <Button
              type="button"
              variant="outline"
              onClick={handleReactivate}
              disabled={isAnyMutationPending}
              className="flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reactivar (poner en pendiente)
            </Button>
          )}
        </section>

        {/* Sección: transferencia de club */}
        <section className="border rounded-xl p-4 space-y-3 bg-gray-50/50">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4" />
            Transferir a otro club
          </h3>
          <Select
            label="Club destino"
            value={targetClubId}
            onChange={(e) => setTargetClubId(e.target.value)}
          >
            {(clubs ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          {clubChanged && targetClub && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Se transferirá de <strong>{reg.clubName}</strong> a{" "}
              <strong>{targetClub.name}</strong>. Los pagos del club original se
              mantienen en su contabilidad.
            </p>
          )}
        </section>

        {/* Sección: intercambiar integrante */}
        {onSwap && !isInactive && (
          <section className="border rounded-xl p-4 space-y-3 bg-gray-50/50">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Intercambiar integrante
            </h3>
            <p className="text-xs text-gray-500">
              Reemplaza a esta persona por otra del mismo club. El que entra
              hereda el precio y el estado de pago (no se cobra diferencia).
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onSwap(reg);
                onClose();
              }}
              disabled={isAnyMutationPending}
              className="flex items-center gap-2"
            >
              <ArrowRightLeft className="w-4 h-4" />
              Intercambiar por otro integrante
            </Button>
          </section>
        )}

        {/* Razón del cambio (auditoría) */}
        <section className="border rounded-xl p-4 bg-blue-50/40 border-blue-200">
          <Input
            label="Razón del cambio (opcional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej: el padre solicitó traslado tras reunión del 12/04"
          />
          <p className="text-xs text-blue-700 mt-1">
            Toda acción queda registrada en el historial de auditoría.
          </p>
        </section>

        {/* Zona peligrosa */}
        <section className="border-2 border-red-200 rounded-xl p-4 bg-red-50/40">
          <h3 className="text-sm font-semibold text-red-800 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" />
            Zona peligrosa
          </h3>
          <Button
            type="button"
            variant="outline"
            onClick={handleDelete}
            disabled={isAnyMutationPending}
            className="text-red-700 border-red-300 hover:bg-red-100 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Eliminar definitivamente
          </Button>
        </section>
      </div>

      <ModalFooter>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isAnyMutationPending}
        >
          Cerrar
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveAll}
          disabled={
            isAnyMutationPending ||
            (!fieldChanges && !statusChanged && !clubChanged)
          }
          className="flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {isAnyMutationPending ? "Guardando…" : "Guardar cambios"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
